/**
 * Spin-out candidates API.
 */
import { apiClient } from '@/services/api/client';

export type SpinoutArchetype =
  | 'solo_founder'
  | 'co_founder'
  | 'psc_migration'
  | 'corporate_carveout'
  | 'team_liftout'
  | 'related_entity';

export type SpinoutCandidateStatus =
  | 'active'
  | 'suppressed'
  | 'human_investigating'
  | 'human_approved'
  | 'human_rejected';

export interface SpinoutCompanyRef {
  id: string;
  name: string;
  registration_number: string;
  jurisdiction_code: string;
  status: string;
  incorporated_on?: string | null;
  industry_codes?: string[] | null;
  fca_status?: string | null;
  logo_url?: string | null;
  domain?: string | null;
}

export interface SpinoutPersona {
  id: string;
  kind: 'cluster' | 'person';
  canonical_name: string;
  member_count: number;
}

export interface SpinoutCandidate {
  id: string;
  archetype: SpinoutArchetype;
  confidence: number;
  status: string;
  detected_at: string;
  detector_version: number;
  parent: SpinoutCompanyRef;
  new_company: SpinoutCompanyRef;
  involved_cluster_ids: string[];
  involved_person_ids: string[];
  personas: SpinoutPersona[];
  evidence: Record<string, unknown> & {
    features?: Record<string, unknown>;
    dates?: Record<string, string | null>;
    identity?: Record<string, unknown>;
  };
}

export interface SpinoutCandidatesResponse {
  results: SpinoutCandidate[];
  pagination: { total: number; offset: number; limit: number };
}

export type SortBy =
  | 'new_incorporated_on'
  | 'parent_incorporated_on'
  | 'confidence'
  | 'detected_at'
  | 'archetype';

export type SortOrder = 'asc' | 'desc';

export interface SpinoutQueryParams {
  archetype?: SpinoutArchetype;
  min_confidence?: number;
  company_id?: string;
  status?: string;
  parent_name_q?: string;
  new_name_q?: string;
  new_incorporated_from?: string;
  new_incorporated_to?: string;
  sort_by?: SortBy;
  sort_order?: SortOrder;
  offset?: number;
  limit?: number;
}

export async function fetchSpinoutCandidates(
  params: SpinoutQueryParams & {
    parent_sic_codes?: string;
    new_sic_codes?: string;
    parent_categories?: string;
    new_categories?: string;
  } = {}
): Promise<SpinoutCandidatesResponse> {
  return apiClient.get<SpinoutCandidatesResponse>('/v1/spinout/candidates', {
    archetype: params.archetype,
    min_confidence: params.min_confidence,
    company_id: params.company_id,
    status: params.status,
    parent_name_q: params.parent_name_q,
    new_name_q: params.new_name_q,
    new_incorporated_from: params.new_incorporated_from,
    new_incorporated_to: params.new_incorporated_to,
    parent_sic_codes: params.parent_sic_codes,
    new_sic_codes: params.new_sic_codes,
    parent_categories: params.parent_categories,
    new_categories: params.new_categories,
    sort_by: params.sort_by,
    sort_order: params.sort_order,
    offset: params.offset ?? 0,
    limit: params.limit ?? 50,
  });
}

// ---------------------------------------------------------------------------
// Persona rollup — one row per person
// ---------------------------------------------------------------------------

export type PersonaSortBy =
  | 'latest_new_incorporated_on'
  | 'max_confidence'
  | 'candidate_count'
  | 'canonical_name';

export interface PersonaRollup {
  persona: SpinoutPersona;
  parent_companies: SpinoutCompanyRef[];
  new_companies: SpinoutCompanyRef[];
  archetypes: SpinoutArchetype[];
  candidate_ids: string[];
  candidate_count: number;
  max_confidence: number;
  min_confidence: number;
  earliest_new_incorporated_on?: string | null;
  latest_new_incorporated_on?: string | null;
  earliest_detected_at: string;
  latest_detected_at: string;
  parent_industry_codes: string[];
  new_industry_codes: string[];
  representative_status?: SpinoutCandidateStatus | null;
}

export interface PersonaRollupsResponse {
  results: PersonaRollup[];
  pagination: { total: number; offset: number; limit: number };
}

export interface PersonaQueryParams {
  archetype?: SpinoutArchetype;
  min_confidence?: number;
  status?: string;
  parent_name_q?: string;
  new_name_q?: string;
  new_incorporated_from?: string;
  new_incorporated_to?: string;
  parent_sic_codes?: string;
  new_sic_codes?: string;
  parent_categories?: string;
  new_categories?: string;
  parent_fca_registered?: boolean;
  new_fca_registered?: boolean;
  sort_by?: PersonaSortBy;
  sort_order?: SortOrder;
  offset?: number;
  limit?: number;
}

export async function fetchSpinoutPersonas(
  params: PersonaQueryParams = {}
): Promise<PersonaRollupsResponse> {
  return apiClient.get<PersonaRollupsResponse>('/v1/spinout/personas', {
    archetype: params.archetype,
    min_confidence: params.min_confidence,
    status: params.status,
    parent_name_q: params.parent_name_q,
    new_name_q: params.new_name_q,
    new_incorporated_from: params.new_incorporated_from,
    new_incorporated_to: params.new_incorporated_to,
    parent_sic_codes: params.parent_sic_codes,
    new_sic_codes: params.new_sic_codes,
    parent_categories: params.parent_categories,
    new_categories: params.new_categories,
    parent_fca_registered: params.parent_fca_registered ? true : undefined,
    new_fca_registered: params.new_fca_registered ? true : undefined,
    sort_by: params.sort_by,
    sort_order: params.sort_order,
    offset: params.offset ?? 0,
    limit: params.limit ?? 50,
  });
}

export type HumanSetStatus =
  | 'active'
  | 'human_investigating'
  | 'human_approved'
  | 'human_rejected';

export interface SpinoutCandidateDetail extends SpinoutCandidate {
  notes?: string | null;
  last_status_change_by_user_id?: string | null;
  last_status_change_at?: string | null;
}

export async function fetchSpinoutCandidate(id: string): Promise<SpinoutCandidateDetail> {
  return apiClient.get<SpinoutCandidateDetail>(`/v1/spinout/candidates/${id}`);
}

export async function updateSpinoutCandidateStatus(
  id: string,
  body: { status: HumanSetStatus; notes?: string | null }
): Promise<SpinoutCandidateDetail> {
  return apiClient.patch<SpinoutCandidateDetail>(`/v1/spinout/candidates/${id}`, body);
}
